import { Text } from '@chakra-ui/layout'
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Heading,
    IconButton
} from '@chakra-ui/react'
import { Layout } from 'components/atoms'
import { Icon } from 'components/atoms/Icon'
import { InputBuild, SelectBuild } from 'components/molecules'
import React, { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { getCEP } from 'services/viacep'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import { AddressForm } from './form'
import { Container, Header } from './styles'
import { AddressKeys } from './types'

const Address: React.FC = () => {
    const [searching, setSearching] = useState<boolean>(false)
    const formDefault = {
        cep: '',
        logradouro: '',
        numero: '',
        municipio: '',
        bairro: '',
        uf: '',
        complemento: '',
        tipoEndereco: ''
    }
    const {
        control,
        register,
        watch,
        formState: { errors },
        trigger
    }: any = useFormContext<{ address: any }>()

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: 'address.form'
    })

    interface FieldError {
        form?: any // Add form property to FieldError
    }

    const form = useMemo(() => AddressForm, [AddressForm])
    const fieldsArray = useMemo(() => fields, [fields])

    const hasError = (index: number, id: AddressKeys): any => {
        const err = errors?.address?.form?.[index]
        return err && err[id]
    }

    const [accordionIndex, setAccordionIndex] = useState(0)
    const handleChange = (index: number): void => {
        setAccordionIndex(index)
    }
    const handleAdd = (): void => {
        append(formDefault)
        setAccordionIndex(fields.length)
    }
    const handleDelete = (index: number): void => {
        remove(index)
    }

    const consultCEP = useDebounce(async (cep: string, index: number) => {
        cep.length === 8 &&
            (await getCEP(cep)
                .then(response => {
                    const { cep, localidade, logradouro, bairro, uf } = response
                    update(index, {
                        ...watch().address.form[index],
                        cep,
                        logradouro,
                        bairro,
                        uf,
                        municipio: localidade
                    })
                    trigger(`address.form.${index}`)
                })
                .finally(() => {
                    setSearching(false)
                }))
    }, 300)

    const handleCEP = (cep: string, index: number) => {
        const value = cep.replace(/\D/g, '')
        setSearching(value.length === 8)
        consultCEP(value, index)
    }

    useEffect(() => {
        !fields.length && handleAdd()
    }, [])

    return (
        <Container type="container" px={8}>
            <Header type="row">
                <Box position="relative">
                    <Heading
                        display="flex"
                        size="md"
                        alignItems="center"
                        justifyContent={{
                            base: 'space-between',
                            sm: 'center'
                        }}
                        minH="32px"
                    >
                        Endereços da instituição financeira
                        <IconButton
                            position={{ sm: 'absolute' }}
                            right="1rem"
                            colorScheme="primary"
                            aria-label="Search database"
                            icon={<Icon name="add" />}
                            onClick={handleAdd}
                        />
                    </Heading>
                </Box>
            </Header>
            <Accordion
                defaultValue={accordionIndex}
                index={accordionIndex}
                allowToggle
                onChange={handleChange}
            >
                {fieldsArray.map((item, index) => (
                    <AccordionItem key={item.id}>
                        <h2>
                            <AccordionButton px={0}>
                                <Box
                                    flex="1"
                                    textAlign="left"
                                    textTransform="capitalize"
                                >
                                    <Text
                                        textAlign="left"
                                        textTransform="capitalize"
                                        fontWeight="bold"
                                    >
                                        Endereço{' '}
                                        {formatter.firstLatterUppercase(
                                            watch(
                                                `address.form.${index}.tipoEndereco`
                                            )
                                        ) || index + 1}
                                    </Text>
                                </Box>
                                {fields.length > 1 && (
                                    <Icon
                                        name="delet"
                                        onClick={() => handleDelete(index)}
                                    />
                                )}

                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel px={0} pb={4}>
                            <Layout type="row" gutter={{ y: 1.5 }} mb={6}>
                                {form.map(({ layout, input, select }, key) => (
                                    <Layout {...layout} key={key}>
                                        {select ? (
                                            <SelectBuild
                                                {...select}
                                                id={`${index}.${select.id}`}
                                                err={hasError(index, select.id)}
                                                {...register(
                                                    `address.form.${index}.${select.id}`
                                                )}
                                                isSearching={
                                                    select.isSearching &&
                                                    searching
                                                }
                                            />
                                        ) : (
                                            input && (
                                                <InputBuild
                                                    {...input}
                                                    id={`${index}.${input.id}`}
                                                    err={hasError(
                                                        index,
                                                        input.id
                                                    )}
                                                    {...register(
                                                        `address.form.${index}.${input.id}`
                                                    )}
                                                    isSearching={
                                                        input.isSearching &&
                                                        searching
                                                    }
                                                    onKeyUp={e =>
                                                        input.id === 'cep' &&
                                                        handleCEP(
                                                            e.currentTarget
                                                                .value as string,
                                                            index
                                                        )
                                                    }
                                                    value={
                                                        watch(
                                                            `address.form.${index}.${input.id}`
                                                        ) as string
                                                    }
                                                />
                                            )
                                        )}
                                    </Layout>
                                ))}
                            </Layout>
                        </AccordionPanel>
                    </AccordionItem>
                ))}
            </Accordion>
        </Container>
    )
}

export default Address
